import type { FrameParams, Renderer } from '../render/renderer.ts';

// Browser canvas ceilings: total area ~268 MP (Chrome/Firefox) and about
// 32k px on a side. Rather than refuse an oversized request, clamp the
// scale to the largest feasible value and report what was rendered.
export const MAX_PX = 260000000;
export const MAX_SIDE = 32000;

export function feasibleScale(rw: number, rh: number): number {
  return Math.max(1, Math.min(Math.sqrt(MAX_PX / (rw * rh)), MAX_SIDE / rw, MAX_SIDE / rh));
}

export function safeName(s: string): string {
  return String(s).replace(/[^A-Za-z0-9._-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

export function stamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate()) + '-' + p(d.getHours()) + p(d.getMinutes()) + p(d.getSeconds());
}

export interface RegionResult {
  canvas: HTMLCanvasElement;
  W: number;
  H: number;
  scale: number;
  clamped: boolean;
}

export async function renderRegion(
  renderer: Renderer,
  p: FrameParams,
  x0: number, y0: number, x1: number, y1: number,
  wantScale: number,
): Promise<RegionResult | null> {
  const rx = Math.min(x0, x1), ry = Math.min(y0, y1);
  const rw = Math.abs(x1 - x0), rh = Math.abs(y1 - y0);
  if (rw < 2 || rh < 2) return null;
  const scale = Math.min(wantScale, feasibleScale(rw, rh));
  const canvas = await renderer.renderRegion(p, rx, ry, rw, rh, scale);
  return { canvas, W: canvas.width, H: canvas.height, scale, clamped: isFinite(wantScale) && scale < wantScale - 1e-9 };
}

export function canvasToBlob(c: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((r) => c.toBlob(r, 'image/png'));
}

export async function downloadCanvas(c: HTMLCanvasElement, name: string): Promise<boolean> {
  const blob = await canvasToBlob(c);
  if (!blob) return false;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return true;
}

export async function copyCanvas(c: HTMLCanvasElement): Promise<boolean> {
  try {
    const blob = await canvasToBlob(c);
    if (!blob) return false;
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    return true;
  } catch {
    return false;
  }
}
